import React from 'react';
import s from './s.module.less';
import { ReviewData, ThirdStepDataType } from '../../types';
import { Button, Input, Popover } from 'antd';
import { SearchOutlined, ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import EditReviewModal from '../EditReviewModal';

type Props = {
    onUpdate: (data?: ReviewData[]) => void;
    data?: ThirdStepDataType;
};

type SortOrder = 'asc' | 'desc' | null;

const ThirdStep = (props: Props) => {
    const { data, onUpdate } = props;
    const [reviews, setReviews] = React.useState<ReviewData[]>([]);
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
    const [showEditModal, setShowEditModal] = React.useState<boolean>(false);
    const [searchText, setSearchText] = React.useState<string>('');
    const [sortOrder, setSortOrder] = React.useState<SortOrder>(null);
    const [showSearchPopover, setShowSearchPopover] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (data?.reviews) {
            setReviews(data.reviews);
        }
    }, [data]);

    const handleRemove = (index: number) => {
        const newReviews = reviews.filter((_, i) => i !== index);
        setReviews(newReviews);
        onUpdate?.(newReviews);
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        setShowEditModal(true);
    };

    const handleSaveEdit = (updatedReview: ReviewData) => {
        if (editingIndex !== null) {
            const newReviews = [...reviews];
            newReviews[editingIndex] = updatedReview;
            setReviews(newReviews);
            onUpdate?.(newReviews);
        }
        setShowEditModal(false);
        setEditingIndex(null);
    };

    const handleCancelEdit = () => {
        setShowEditModal(false);
        setEditingIndex(null);
    };

    // 处理搜索
    const handleSearch = (value: string) => {
        setSearchText(value);
    };

    // 处理排序
    const handleSort = () => {
        if (sortOrder === null) {
            setSortOrder('desc');
        } else if (sortOrder === 'desc') {
            setSortOrder('asc');
        } else {
            setSortOrder(null);
        }
    };

    // 过滤和排序后的数据
    const filteredAndSortedReviews = React.useMemo(() => {
        let result = [...reviews];

        // 搜索过滤
        if (searchText.trim()) {
            result = result.filter((review) =>
                review.reviewContent.toLowerCase().includes(searchText.toLowerCase()),
            );
        }

        // 排序
        if (sortOrder) {
            result.sort((a, b) => {
                const dateA = new Date(a.date).getTime();
                const dateB = new Date(b.date).getTime();
                if (sortOrder === 'asc') {
                    return dateA - dateB;
                } else {
                    return dateB - dateA;
                }
            });
        }

        return result;
    }, [reviews, searchText, sortOrder]);

    return (
        <div className={s.wrap}>
            <div className={s.summary}>
                <h3>Upload summary</h3>
                <ul className={s.des}>
                    <li><span className={s.highlight}>{data?.importedAmount || 0}</span> reviews ready to upload</li>
                    {(data?.failedAmount && data.failedAmount > 0) ? (
                        <li><span className={s.highlight}>{data.failedAmount}</span> reviews failed</li>
                    ) : null}
                </ul>
            </div>

            {reviews.length > 0 && (
                <div className={s.tableWrap}>
                    <div className={s.table}>
                        <div className={s.tableHeader}>
                            <div className={s.colNumber} />
                            <div className={s.colContent}>
                                <div className={s.headerContent}>
                                    <span>Review details</span>
                                    <Popover
                                        content={
                                            <div className={s.searchPopover}>
                                                <Input
                                                    placeholder="Search reviews..."
                                                    value={searchText}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    prefix={<SearchOutlined />}
                                                    allowClear
                                                />
                                            </div>
                                        }
                                        trigger="click"
                                        open={showSearchPopover}
                                        onOpenChange={setShowSearchPopover}
                                        placement="bottomLeft"
                                    >
                                        <SearchOutlined className={s.searchIcon} />
                                    </Popover>
                                </div>
                            </div>
                            <div className={s.colDate}>
                                <div className={s.headerContent}>
                                    <span>Date</span>
                                    <div className={s.sortIcon} onClick={handleSort}>
                                        {sortOrder === 'desc' && <ArrowDownOutlined />}
                                        {sortOrder === 'asc' && <ArrowUpOutlined />}
                                        {sortOrder === null && (
                                            <ArrowDownOutlined className={s.sortIconInactive} />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={s.colActions} />
                            <div className={s.colActions} />
                        </div>
                        {filteredAndSortedReviews.map((review, displayIndex) => {
                            // 找到原始索引，用于删除和编辑
                            const originalIndex = reviews.findIndex((r) => r === review);
                            return (
                                <div key={originalIndex} className={s.tableRow}>
                                    <div className={s.colNumber}>{displayIndex + 1}</div>
                                    <div className={s.colContent}>{review.reviewContent}</div>
                                    <div className={s.colDate}>{review.date}</div>
                                    <div className={s.colActions}>
                                        <Button
                                            type="link"
                                            onClick={() => handleRemove(originalIndex)}
                                            className={s.actionBtn}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                    <div className={s.colActions}>
                                        <Button
                                            type="link"
                                            onClick={() => handleEdit(originalIndex)}
                                            className={s.actionBtn}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <EditReviewModal
                show={showEditModal}
                review={editingIndex !== null ? reviews[editingIndex] : undefined}
                onCancel={handleCancelEdit}
                onSave={handleSaveEdit}
            />
        </div>
    );
};

export default ThirdStep;
